import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CircleNotch, Monitor, DeviceMobile, DeviceTablet, Globe, Trash, SignOut,
  CheckCircle, Warning, CaretDown,
} from '@phosphor-icons/react';

import type { ManifestSection, SettingsManifest } from '@/lib/settingsApi';
import {
  fetchSessions, revokeSession, revokeAllOtherSessions, changePassword,
  fetchLoginHistory, type SessionInfo,
} from '@/lib/meApi';
import { LoginMethodsSection } from '@/components/settings/LoginMethodsSection';
import { MfaPanel } from '@/components/settings/MfaPanel';
import { PasswordChangeFields } from '@/components/shared/PasswordChangeFields';
import { emptyPasswordForm, isPasswordFormReady } from '@/lib/passwordPolicy';
import { Panel, SaveIndicator, SectionIntro, useSaveState, useSettingLookup } from './primitives';

/**
 * Security — password, how you sign in, where you are signed in, and what has happened.
 *
 * TWO-FACTOR now ships whole, which is the bar the previous note in this header set and would
 * not waive. Enrolment, recovery codes, a login challenge that completes, and a way back in
 * without the phone all exist — `MfaPanel` is the surface, `apps/api/src/lib/mfa.ts` is the one
 * implementation behind it. The panel is rendered only when the manifest offers `security.mfa`,
 * so who is allowed to see it is still the server's decision, not this component's.
 */

/**
 * `::ffff:127.0.0.1` is an IPv4 address carried inside IPv6. The prefix is protocol trivia that
 * appeared on every row of both lists and told the reader nothing.
 */
function cleanIp(ip: string | null | undefined) {
  return ip ? ip.replace(/^::ffff:/, '') : null;
}

/**
 * Both lists on this page are unbounded — every live session, and the 25 sign-in events the
 * server returns. Rendered in full they turned Security into a very long scroll on a phone, so
 * each collapses to its most recent few and opens on request.
 */
function ShowMore({ expanded, hidden, noun, onToggle }: {
  expanded: boolean; hidden: number; noun: string; onToggle: () => void;
}) {
  if (hidden <= 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full rounded-lg border border-border/60 py-2 text-xs font-medium text-muted-foreground transition hover:bg-muted/40 hover:text-foreground"
    >
      {expanded ? 'Show fewer' : `Show ${hidden} more ${noun}`}
    </button>
  );
}

function parseDevice(ua: string | null) {
  if (!ua || ua.includes('curl')) return { name: 'API client', os: '', Icon: Globe };
  const name = ua.includes('Firefox') ? 'Firefox'
    : ua.includes('Edg') ? 'Edge'
    : ua.includes('Chrome') ? 'Chrome'
    : ua.includes('Safari') ? 'Safari'
    : 'Browser';
  const os = ua.includes('Windows') ? 'Windows'
    : ua.includes('Mac OS') ? 'macOS'
    : ua.includes('iPhone') || ua.includes('iPad') ? 'iOS'
    : ua.includes('Android') ? 'Android'
    : ua.includes('Linux') ? 'Linux'
    : '';
  const mobile = ua.includes('iPhone') || ua.includes('Android') || ua.includes('Mobile');
  return { name, os, Icon: mobile ? DeviceMobile : ua.includes('Tablet') ? DeviceTablet : Monitor };
}

function when(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function PasswordPanel({ canWrite }: { canWrite: boolean }) {
  const [form, setForm] = useState(emptyPasswordForm);
  const { state, error, run } = useSaveState();

  const ready = isPasswordFormReady(form);

  async function submit() {
    if (!ready) return;
    const ok = await run(() => changePassword(form.current, form.next));
    if (ok) setForm(emptyPasswordForm);
  }

  if (!canWrite) {
    return (
      <Panel title="Password" description="How you sign in">
        <p className="text-sm text-muted-foreground">Your password is not managed from this screen.</p>
      </Panel>
    );
  }

  return (
    <Panel title="Password" description="Use something long, and used nowhere else">
      <div className="max-w-md space-y-3">
        {/*
          The fields, the strength meter and the rules come from ONE shared component, also used by
          the forced first-sign-in change (ForcePasswordChangeModal). They used to be written out
          here; a second copy would have drifted from this one the first time either changed.
        */}
        <PasswordChangeFields value={form} onChange={setForm} disabled={state === 'saving'} />

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!ready || state === 'saving'}
            className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            {state === 'saving' ? 'Updating…' : 'Update password'}
          </button>
          <SaveIndicator state={state === 'saved' ? 'saved' : state} error={error} />
        </div>
      </div>
    </Panel>
  );
}

function SessionsPanel() {
  const qc = useQueryClient();
  const { data: sessions = [], isLoading } = useQuery({ queryKey: ['me-sessions'], queryFn: fetchSessions });
  const [busy, setBusy] = useState<string | null>(null);
  const { state, error, run } = useSaveState();

  const current = sessions.find((s) => s.isCurrent);
  const others = sessions.filter((s) => !s.isCurrent);
  const [allSessions, setAllSessions] = useState(false);
  /** Closed by default, like the sign-in history below it. */
  const [open, setOpen] = useState(false);
  const SESSION_PREVIEW = 4;
  const shownOthers = allSessions ? others : others.slice(0, SESSION_PREVIEW);

  async function revoke(id: string) {
    setBusy(id);
    await run(async () => {
      await revokeSession(id);
      qc.setQueryData(['me-sessions'], (old: SessionInfo[] | undefined) => (old ?? []).filter((s) => s.id !== id));
    });
    setBusy(null);
  }

  return (
    <Panel
      title="Where you are signed in"
      description="Every device holding a live session on your account"
      action={
        <div className="flex shrink-0 items-center gap-2">
          {/* Offered only while the list is open: signing out of every other device without
              being able to see them is acting blind. */}
          {open && others.length > 0 && (
            <button
              type="button"
              onClick={() => void run(async () => { await revokeAllOtherSessions(); await qc.invalidateQueries({ queryKey: ['me-sessions'] }); })}
              disabled={state === 'saving'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:text-red-600 dark:hover:text-red-400"
            >
              <SignOut size={12} weight="bold" /> Sign out everywhere else
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? 'Hide signed-in devices' : 'Show signed-in devices'}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
          >
            <span>{isLoading ? '…' : sessions.length}</span>
            <CaretDown size={12} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      }
    >
      {!open ? null : isLoading ? (
        <div className="flex h-20 items-center justify-center"><CircleNotch size={18} className="animate-spin text-muted-foreground/40" /></div>
      ) : (
        <div className="space-y-2">
          {current && (() => {
            const d = parseDevice(current.userAgent);
            return (
              <div className="flex items-center gap-3 rounded-lg border border-brand-500/30 bg-brand-50/40 px-4 py-3 dark:bg-brand-500/10">
                <d.Icon size={16} className="text-brand-600 dark:text-brand-400" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                    {d.name}{d.os && <span className="font-normal text-muted-foreground">· {d.os}</span>}
                    <span className="rounded-full bg-brand-500/15 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:text-brand-300">This device</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{cleanIp(current.ip) ?? 'Unknown address'} · active now</div>
                </div>
              </div>
            );
          })()}

          {shownOthers.map((s) => {
            const d = parseDevice(s.userAgent);
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <d.Icon size={16} className="shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {d.name}{d.os && <span className="font-normal text-muted-foreground"> · {d.os}</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {cleanIp(s.ip) ?? 'Unknown address'} · signed in {when(s.createdAt)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void revoke(s.id)}
                  disabled={busy === s.id}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition hover:text-red-600 dark:hover:text-red-400"
                >
                  {busy === s.id ? <CircleNotch size={11} className="animate-spin" /> : <><Trash size={11} /> Sign out</>}
                </button>
              </div>
            );
          })}

          <ShowMore
            expanded={allSessions}
            hidden={others.length - SESSION_PREVIEW}
            noun="sessions"
            onToggle={() => setAllSessions((v) => !v)}
          />

          {sessions.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">No other sessions.</p>}
          <SaveIndicator state={state === 'saved' ? 'idle' : state} error={error} />
        </div>
      )}
    </Panel>
  );
}

/** Narrowing is over the rows already on screen — no new request, no server or schema change. */
type HistoryFilter = 'all' | 'login' | 'failed_login';

const HISTORY_FILTERS: { key: HistoryFilter; label: string }[] = [
  { key: 'all',          label: 'All' },
  { key: 'login',        label: 'Sign-ins' },
  { key: 'failed_login', label: 'Failed attempts' },
];

function LoginHistoryPanel() {
  const { data, isLoading } = useQuery({ queryKey: ['me-login-history'], queryFn: fetchLoginHistory });
  const [allEvents, setAllEvents] = useState(false);
  const [filter, setFilter] = useState<HistoryFilter>('all');
  /** Closed by default — this is a record to consult, not something to read every visit. */
  const [open, setOpen] = useState(false);
  const EVENT_PREVIEW = 5;
  const events = data?.events ?? [];

  /**
   * Filtering happens HERE, over the rows already fetched — the request, its 25-row server cap
   * and the audit log behind it are all untouched. Nothing is hidden permanently: 'All' is the
   * default, so no event is ever missing unless the reader chose to narrow the list.
   */
  const counts = {
    all: events.length,
    login: events.filter((e) => e.action === 'login').length,
    failed_login: events.filter((e) => e.action === 'failed_login').length,
  };
  const filtered = filter === 'all' ? events : events.filter((e) => e.action === filter);
  const shownEvents = allEvents ? filtered : filtered.slice(0, EVENT_PREVIEW);

  return (
    <Panel
      title="Sign-in history"
      description="Recent attempts on your account, successful or not"
      action={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Hide sign-in history' : 'Show sign-in history'}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
        >
          {/* A failed attempt is the one thing this panel exists to surface, so it is shown
              even while collapsed — closing the list must never hide a warning. */}
          {counts.failed_login > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-500/15 dark:text-red-300">
              <Warning size={9} weight="fill" /> {counts.failed_login} failed
            </span>
          )}
          {/* Never print "0" while the count is still loading — that is a wrong number, not an
              empty one. */}
          <span>{isLoading ? '…' : events.length}</span>
          <CaretDown size={12} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      }
    >
      {!open ? null : isLoading ? (
        <div className="flex h-20 items-center justify-center"><CircleNotch size={18} className="animate-spin text-muted-foreground/40" /></div>
      ) : events.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">Nothing recorded yet.</p>
      ) : (
        <>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {HISTORY_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  filter === f.key
                    ? 'bg-foreground text-background'
                    : 'border border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                {f.label} <span className="opacity-60">{counts[f.key]}</span>
              </button>
            ))}
          </div>
          {/* A filter that matches nothing must say so. "Nothing recorded yet" here would be a
              lie — the events exist, they just are not of the kind being asked for. */}
          {filtered.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              No {filter === 'failed_login' ? 'failed attempts' : 'sign-ins'} in the recent history.
            </p>
          ) : (
        <ul className="divide-y divide-border/60">
          {shownEvents.map((e) => {
            const failed = e.action === 'failed_login';
            return (
              <li key={e.id} className="flex items-start justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    {failed ? (
                      <Warning size={13} weight="fill" className="shrink-0 text-red-500" />
                    ) : (
                      <CheckCircle size={13} weight="fill" className="shrink-0 text-emerald-500" />
                    )}
                    <span className="font-medium text-foreground">
                      {failed ? 'Failed sign-in' : e.action === 'logout' ? 'Signed out' : 'Signed in'}
                    </span>
                    {e.method && (
                      <span className="text-xs text-muted-foreground">via {e.method.replace(/_/g, ' ')}</span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate pl-5 text-xs text-muted-foreground">
                    {cleanIp(e.ip) ?? 'Unknown address'}
                    {e.userAgent && <> · {parseDevice(e.userAgent).name} {parseDevice(e.userAgent).os}</>}
                  </div>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">{when(e.at)}</span>
              </li>
            );
          })}
        </ul>
          )}
          <ShowMore
            expanded={allEvents}
            hidden={filtered.length - EVENT_PREVIEW}
            noun="entries"
            onToggle={() => setAllEvents((v) => !v)}
          />
        </>
      )}
    </Panel>
  );
}

export function SecuritySection({ section }: { section: ManifestSection; manifest: SettingsManifest }) {
  const setting = useSettingLookup(section);
  const password = setting('security.password');

  return (
    <div className="space-y-5">
      <SectionIntro title="Security" description="Your password, your devices, and how you sign in" />

      {password && <PasswordPanel canWrite={password.canWrite} />}
      {setting('security.loginMethods') && <LoginMethodsSection />}
      {setting('security.sessions') && <SessionsPanel />}
      {setting('security.loginHistory') && <LoginHistoryPanel />}
      {(setting('security.mfa') || setting('security.recoveryCodes')) && <MfaPanel />}
    </div>
  );
}
